import { MouseEvent } from 'react'

import { ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material'

import { BOARD_THEME_BG, BOARD_THEME_LINE, BOARD_THEME_MUTED, BOARD_THEMES, BoardTheme } from './boardTheme'

type Props = {
  value: BoardTheme
  onChange: (theme: BoardTheme) => void
  // The words come in from the page, as the public shared page has only its own messages.
  labels: { group: string } & Record<BoardTheme, string>
}

// Auto, Light and Dark, one always on: the theme the viewer sees board pages in.
export default function ThemeSwitch({ value, onChange, labels }: Props) {
  const choose = (_: MouseEvent<HTMLElement>, next: BoardTheme | null) => {
    if (next) onChange(next)
  }

  return (
    <ToggleButtonGroup
      exclusive
      size='small'
      value={value}
      onChange={choose}
      aria-label={labels.group}
      data-cy='boardTheme'
    >
      {BOARD_THEMES.map(theme => (
        <ToggleButton
          key={theme}
          value={theme}
          sx={{ py: 0.25, px: 1.25, textTransform: 'none' }}
          data-cy={`boardTheme-${theme}`}
        >
          {labels[theme]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

// The theme the board's frames show: Auto is the OS's, read live.
export const useResolvedBoardTheme = (theme: BoardTheme): Exclude<BoardTheme, 'auto'> => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true })
  if (theme !== 'auto') return theme

  return prefersDark ? 'dark' : 'light'
}

// The page's colour behind the frames: the --bg of the theme they show.
export const useBoardBackground = (theme: BoardTheme) => BOARD_THEME_BG[useResolvedBoardTheme(theme)]

// What the page draws around the frames, in the theme they show: its --bg behind them, its own words in --muted,
// and a block still loading in --line. The light MUI defaults would hardly show on the dark --bg.
export const useBoardColours = (theme: BoardTheme) => {
  const resolved = useResolvedBoardTheme(theme)

  return {
    background: BOARD_THEME_BG[resolved],
    text: BOARD_THEME_MUTED[resolved],
    placeholder: BOARD_THEME_LINE[resolved],
  }
}
